import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Bank, ChartBar, Command as CmdIcon, Cube, FileText, Gauge, Gear, Moon, Receipt,
  ArrowsLeftRight, ArrowUUpLeft,
  ShieldCheck, SignOut, Sparkle, Sun, Users, Wallet,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import CommandPalette from "@/components/CommandPalette";
import AiDrawer from "@/components/AiDrawer";
import DraftDialog from "@/components/DraftDialog";
import { useApp } from "@/context/AppContext";

const RAIL = [
  { to: "/", icon: Gauge, label: "Home" },
  { to: "/vouchers", icon: Receipt, label: "Vouchers" },
  { to: "/notes", icon: ArrowUUpLeft, label: "Notes", tip: "Credit & Debit Notes" },
  { to: "/masters/parties", icon: Users, label: "Parties" },
  { to: "/masters/ledgers", icon: Wallet, label: "Ledgers", tip: "Chart of Accounts" },
  { to: "/masters/items", icon: FileText, label: "Items" },
  { to: "/inventory", icon: Cube, label: "Inventory" },
  { to: "/banking", icon: Bank, label: "Banking" },
  { to: "/compliance", icon: ShieldCheck, label: "Compliance" },
  { to: "/reports", icon: ChartBar, label: "Reports" },
  { to: "/copilot", icon: Sparkle, label: "Copilot", tip: "AI Copilot" },
  { to: "/migration", icon: ArrowsLeftRight, label: "Migration", tip: "Migration Wizard" },
  { to: "/settings", icon: Gear, label: "Settings" },
];

export default function Shell() {
  const { user, company, companies, switchCompany, logout, theme, setTheme } = useApp();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const navigate = useNavigate();

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-screen overflow-hidden bg-background">
        {/* Left rail */}
        <nav className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-border bg-card py-3" data-testid="left-rail">
          <div className="mb-3 flex h-8 w-8 items-center justify-center bg-primary font-display text-sm font-extrabold text-primary-foreground">
            FX
          </div>
          {RAIL.map((r) => (
            <Tooltip key={r.to}>
              <TooltipTrigger asChild>
                <NavLink to={r.to} end={r.to === "/"} data-testid={`rail-${r.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {({ isActive }) => (
                    <span className="rail-item" data-active={isActive}>
                      <r.icon size={19} />
                    </span>
                  )}
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">{r.tip || r.label}</TooltipContent>
            </Tooltip>
          ))}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur-xl">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="company-switcher"
                  className="flex items-center gap-2 border border-border px-2.5 py-1 text-xs font-medium hover:border-primary/60"
                  style={{ transitionProperty: "border-color" }}
                >
                  <span className="truncate max-w-[200px]">{company?.legal_name || "Select company"}</span>
                  <span className="num text-[10px] text-muted-foreground">{company?.gstin || "No GSTIN"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Companies</DropdownMenuLabel>
                {(companies || []).map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => switchCompany(c)} data-testid={`company-option-${c.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm">{c.legal_name}</p>
                      <p className="num text-[10px] text-muted-foreground">{c.gstin || "—"}</p>
                    </div>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/setup")} data-testid="add-company-btn">
                  + Add company
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => setPaletteOpen(true)}
              data-testid="cmd-k-trigger"
              className="flex flex-1 items-center gap-2 border border-border px-3 py-1 text-left text-xs text-muted-foreground hover:border-primary/60 hover:text-foreground"
              style={{ transitionProperty: "color, border-color" }}
            >
              <CmdIcon size={13} />
              <span className="flex-1 truncate">Search, navigate, or describe a transaction…</span>
              <kbd className="num border border-border px-1 text-[10px]">⌘K</kbd>
            </button>

            <Button
              size="sm"
              variant={drawerOpen ? "default" : "outline"}
              className="h-7 gap-1.5 text-xs"
              onClick={() => setDrawerOpen((o) => !o)}
              data-testid="ai-drawer-toggle"
            >
              <Sparkle size={14} /> Copilot
            </Button>

            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              data-testid="theme-toggle"
              className="text-muted-foreground hover:text-foreground"
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="flex h-7 w-7 items-center justify-center bg-secondary font-display text-xs font-bold"
                >
                  {(user?.name || "U").slice(0, 2).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm">{user?.name}</p>
                  <p className="text-[11px] font-normal text-muted-foreground">{user?.email}</p>
                  <p className="mt-1 inline-block bg-primary/10 px-1.5 text-[10px] uppercase tracking-widest text-primary">
                    {user?.role}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  Settings & Team
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
                  <SignOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <div className="flex min-h-0 flex-1">
            <main className="min-w-0 flex-1 overflow-y-auto p-6" data-testid="main-content">
              <Outlet />
            </main>
            <AiDrawer open={drawerOpen} setOpen={setDrawerOpen} />
          </div>
        </div>

        <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} onDraft={setDraft} />
        <DraftDialog draft={draft} onClose={() => setDraft(null)} />
      </div>
    </TooltipProvider>
  );
}
